import { FC, useCallback } from "react";
import { PopoverProps } from "@mui/material";
import { AsyncThunk } from "@reduxjs/toolkit";
import { WidgetGroupingChip } from "@/components/pages/dashboard/components/widgets/common/WidgetGroupingChip";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { GroupingSelector } from "@/components/pages/common/resource-explorer-grouping-selector-v2/GroupingSelector";
import { WidgetQuickActionTrigger } from "@/components/pages/dashboard/components/adding-widget/widget-create/widget-setups/common/WidgetQuickActionTrigger";
import {
  ResourceExplorerGroupingLabel,
  ResourceExplorerGroupingNullable,
} from "@/services/cloudchipr.api";
import { DashboardWidgetType } from "@/store/dashboards/utils/types/types";
import { useAppAbility } from "@/services/permissions";
import { currentDashboardProtectedSelector } from "@/store/dashboards/selectors/dashboard-hierarchy-visibility/currentDashboardProtectedSelector";
import { isDeepEqual } from "@/utils/is-deep-equal";

export type WidgetGroupByUpdateThunkType = AsyncThunk<
  any,
  Partial<DashboardWidgetType>,
  any
>;

interface WidgetHeaderGroupBySelectProps {
  widgetId: string;
  groupValues?: string[];
  groupBy?: ResourceExplorerGroupingNullable;
  groupByLabel?: ResourceExplorerGroupingLabel;
  updateThunk: WidgetGroupByUpdateThunkType;
}

export const WidgetHeaderGroupBySelect: FC<WidgetHeaderGroupBySelectProps> = ({
  widgetId,
  groupBy,
  groupValues,
  groupByLabel,
  updateThunk,
}) => {
  const dispatch = useAppDispatch();
  const { cannot } = useAppAbility();
  const canNotEditWidget = cannot("edit", "dashboard");
  const isProtected = useAppSelector(currentDashboardProtectedSelector);

  const groupingChangeHandler = useCallback(
    (
      newGroupBy: ResourceExplorerGroupingNullable,
      newGroupValues?: string[],
    ) => {
      const groupByIsNotChanged = groupBy === newGroupBy;
      const groupValuesIsNotChanged =
        newGroupValues &&
        groupValues &&
        isDeepEqual(newGroupValues, groupValues);

      if (
        groupByIsNotChanged &&
        ((!newGroupValues && !groupValues) || groupValuesIsNotChanged)
      ) {
        return;
      }

      dispatch(
        updateThunk({
          id: widgetId,
          grouping: newGroupBy,
          group_values: newGroupValues,
        }),
      );
    },
    [dispatch, groupBy, groupValues, widgetId, updateThunk],
  );

  if (!groupBy) {
    return;
  }

  return (
    <WidgetGroupingChip
      groupBy={groupBy}
      groupValues={groupValues}
      groupByLabel={groupByLabel}
    >
      {!canNotEditWidget && !isProtected && (
        <GroupingSelector
          width={250}
          value={groupBy}
          groupValues={groupValues}
          PopoverProps={popoverProps}
          hiddenGroupings={["resource"]}
          onChange={groupingChangeHandler}
          CustomTrigger={WidgetQuickActionTrigger}
        />
      )}
    </WidgetGroupingChip>
  );
};

const popoverProps: Partial<PopoverProps> = {
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "right",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "right",
  },
};
